import { Button } from "@components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
} from "@components/ui/input-group";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CopyIcon,
  PlusIcon,
  RefreshCcwIcon,
  Settings2Icon,
  Trash2Icon,
} from "lucide-react";
import {
  Controller,
  UseFieldArrayReturn,
  UseFormReturn,
  useFormState,
  useWatch,
} from "react-hook-form";
import { Textarea } from "@components/ui/textarea";
import { useSearchContext } from "../../context";
import { PreviewFormSubmitButton, SearchPreviewFormType } from "../types";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { i18n } from "@lingui/core";
import {
  Language,
  Source,
  LANGUAGE_CODE_TO_LABEL,
  SOURCE_CODE_TO_LABEL,
  MentionSourceType,
  MENTION_SOURCE_TYPES,
  MENTION_SOURCE_TYPE_TO_LABEL,
  SOURCE,
  MENTION_SOURCE_TYPE,
} from "@/constants";
import BadgeInput from "@components/ui/badge-input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Switch } from "@components/ui/switch";
import { Tag } from "@/types/Tag";
import TagSelector from "./partials/TagSelector";
import { useState } from "react";
import { useOrganization } from "@/hooks/useOrganization";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { plural } from "@lingui/core/macro";
import { Input } from "@components/ui/input";
import { UnsavedQuery } from "@/types/SearchQuery";
import {
  getMinReachByChannelTypeDefaultValues,
  MinReachByChannelTypeSource,
  MinReachByChannelTypeValues,
} from "../reuestUtils";
import TimePeriodField from "./partials/TimePeriodField";
import { ButtonGroup } from "@components/ui/button-group";
import { useIntercom } from "@/hooks/useIntercom";
import { INTERCOM_ARTICLES } from "@/intercom";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";

const DISTANCE_VALUES = [1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 40, 50];

type QueryProps = {
  queriesField: UseFieldArrayReturn<SearchPreviewFormType, "queries">;
  minReachByChannelTypeSources: MinReachByChannelTypeSource[];
  form: UseFormReturn<SearchPreviewFormType>;
  socialSources: Source[];
  audioSources: Source[];
  languages: Language[];
  newsSources: Source[];
  queries: UnsavedQuery[];
  sources: Source[];
  index: number;
  tags: Tag[];
};

export default function Query({
  minReachByChannelTypeSources,
  index: queryIndex,
  socialSources,
  audioSources,
  queriesField,
  newsSources,
  languages,
  sources,
  queries,
  tags,
  form,
}: QueryProps) {
  const { t } = useLingui();
  const intercom = useIntercom();
  const formState = useFormState({ control: form.control });

  // Context
  const { defaultQuery, isLoading } = useSearchContext();

  // Server state
  const { data: orgData } = useOrganization();
  const { plan: { max_num_queries_per_search = 0 } = {} } = orgData?.ok
    ? orgData.data
    : {};

  // Helpers
  const isFirst = !queryIndex;
  const isLast = queryIndex === queriesField.fields.length - 1;
  const canAddQuery =
    max_num_queries_per_search === 0 ||
    queries.length < max_num_queries_per_search;

  // Local state
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
  const [newTags, setNewTags] = useState<string[]>([]);
  const [isNearDistanceSelectOpen, setIsNearDistanceSelectOpen] =
    useState<boolean>(false);
  const [isNotNearDistanceSelectOpen, setIsNotNearDistanceSelectOpen] =
    useState<boolean>(false);

  const nearNearKeywords = useWatch({
    control: form.control,
    name: `queries.${queryIndex}.near_near_keywords`,
  });

  function toggledAdvanced(value: boolean) {
    setIsAdvancedOpen(value);

    if (value) {
      postTrackingData(EVENT.SEARCH_QUERY_ADVANCED_OPENED);
    } else {
      postTrackingData(EVENT.SEARCH_QUERY_ADVANCED_CLOSED);
    }
  }

  function addQuery() {
    queriesField.insert(queryIndex + 1, defaultQuery);

    postTrackingData(EVENT.SEARCH_QUERY_ADDED);
  }

  function deleteQuery() {
    queriesField.remove(queryIndex);

    postTrackingData(EVENT.SEARCH_QUERY_DELETED);
  }

  function duplicateQuery() {
    const upToDateQuery = form.getValues(`queries.${queryIndex}`);
    // Copy settings but not keywords
    queriesField.append({
      ...upToDateQuery,
      keywords: [],
      near_keywords: [],
      not_near_keywords: [],
      near_near_keywords: null,
    });

    postTrackingData(EVENT.SEARCH_QUERY_DUPLICATED);
  }

  return (
    <FieldGroup>
      <div className="grid grid-cols-2 gap-x-4 gap-y-5 items-start">
        <Controller
          name={`queries.${queryIndex}.keywords`}
          control={form.control}
          render={({ field, fieldState }) => {
            const id = `form-qb-queries-${queryIndex}-keywords`;

            return (
              <Field
                data-invalid={fieldState.invalid}
                className={queryIndex ? "col-span-2" : "col-span-1"}
              >
                <FieldLabel htmlFor={id}>
                  <Trans>Sökord</Trans>
                </FieldLabel>
                <BadgeInput
                  value={field.value || []}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  inputProps={{
                    placeholder: field.value?.length
                      ? undefined
                      : t`Ange det varumärke, den organisation eller det ämne du vill bevaka. Tryck Enter efter varje sökord.`,
                    id,
                    "aria-invalid": fieldState.invalid,
                    autoComplete: "off",
                    className: "keywords",
                  }}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        {isFirst ? <TimePeriodField form={form} /> : null}

        <Controller
          name={`queries.${queryIndex}.near_keywords`}
          control={form.control}
          render={({ field, fieldState }) => {
            const id = `form-qb-queries-${queryIndex}-near-keywords`;

            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Sambandsord</Trans>
                </FieldLabel>
                <InputGroup className="h-auto">
                  <BadgeInput
                    withBorder={false}
                    variant="cyan"
                    value={field.value || []}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    inputProps={{
                      placeholder: field.value?.length
                        ? undefined
                        : t`Ange ord som måste förekomma tillsammans med sökorden`,
                      id,
                      "aria-invalid": fieldState.invalid,
                      autoComplete: "off",
                    }}
                  />

                  <Controller
                    name={`queries.${queryIndex}.near_distance`}
                    control={form.control}
                    render={({
                      field: distanceField,
                      fieldState: distanceFieldState,
                    }) => {
                      const distanceId = `form-qb-queries-${queryIndex}-near-distance`;
                      return (
                        <Popover>
                          <InputGroupAddon align="inline-end">
                            <PopoverTrigger
                              render={
                                <InputGroupButton
                                  aria-invalid={distanceFieldState.invalid}
                                  aria-label={t`Inställningar`}
                                  title={t`Inställningar`}
                                  size="icon-xs"
                                />
                              }
                            >
                              <Settings2Icon />
                            </PopoverTrigger>
                          </InputGroupAddon>
                          <PopoverContent className="w-96 flex flex-col gap-4 items-start">
                            <Field data-invalid={distanceFieldState.invalid}>
                              <FieldLabel htmlFor={distanceId}>
                                <Trans>Avstånd</Trans>
                              </FieldLabel>

                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                  <Trans>Högst</Trans>
                                </span>
                                <Select
                                  value={distanceField.value?.toString() || ""}
                                  open={isNearDistanceSelectOpen}
                                  onOpenChange={setIsNearDistanceSelectOpen}
                                  onValueChange={(value) => {
                                    if (!value) {
                                      return;
                                    }

                                    distanceField.onChange(parseInt(value, 10));
                                  }}
                                >
                                  <SelectTrigger
                                    id={distanceId}
                                    aria-invalid={distanceFieldState.invalid}
                                  >
                                    <SelectValue
                                      placeholder={t`Välj avstånd`}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DISTANCE_VALUES.map((value) => (
                                      <SelectItem
                                        key={value}
                                        value={value.toString()}
                                      >
                                        {value}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <span className="text-sm text-muted-foreground truncate">
                                  {plural(distanceField.value || 0, {
                                    one: "ord bort från sökorden",
                                    other: "ord bort från sökorden",
                                  })}
                                </span>
                              </div>

                              {distanceFieldState.invalid && (
                                <FieldError
                                  errors={[distanceFieldState.error]}
                                />
                              )}
                            </Field>

                            {queries[queryIndex]?.near_near_keywords ? null : (
                              <Button
                                variant="secondary"
                                type="button"
                                size="sm"
                                data-testid="form-qb-queries-near-near-keywords-add-button"
                                onClick={() => {
                                  form.setValue(
                                    `queries.${queryIndex}.near_near_keywords`,
                                    []
                                  );
                                }}
                              >
                                <PlusIcon />
                                <Trans>I närheten av</Trans>
                              </Button>
                            )}
                          </PopoverContent>
                        </Popover>
                      );
                    }}
                  />
                </InputGroup>

                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        {nearNearKeywords ? (
          <Controller
            name={`queries.${queryIndex}.near_near_keywords`}
            control={form.control}
            render={({ field, fieldState }) => {
              const id = `form-qb-queries-${queryIndex}-near-near-keywords`;

              return (
                <Field data-invalid={fieldState.invalid} className="self-end">
                  <FieldLabel htmlFor={id} className="sr-only">
                    <Trans>Ord att inkludera</Trans>
                  </FieldLabel>
                  <InputGroup className="h-auto">
                    <BadgeInput
                      withBorder={false}
                      variant="cyan"
                      data-testid={id}
                      value={field.value || []}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      inputProps={{
                        placeholder: field.value?.length
                          ? undefined
                          : t`Ange ord som måste förekomma i samband med dina sambandsord`,
                        id,
                        "aria-invalid": fieldState.invalid,
                        autoComplete: "off",
                      }}
                    />

                    <InputGroupAddon align="inline-end">
                      <InputGroupButton
                        aria-label={t`Ta bort`}
                        title={t`Ta bort`}
                        size="icon-xs"
                        type="button"
                        onClick={() => {
                          field.onChange(null);
                        }}
                      >
                        <Trash2Icon />
                      </InputGroupButton>
                    </InputGroupAddon>
                  </InputGroup>

                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              );
            }}
          />
        ) : null}

        <Controller
          name={`queries.${queryIndex}.not_near_keywords`}
          control={form.control}
          render={({ field, fieldState }) => {
            const id = `form-qb-queries-${queryIndex}-not-near-keywords`;

            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Exkludera ord</Trans>
                </FieldLabel>
                <InputGroup className="h-auto">
                  <BadgeInput
                    withBorder={false}
                    variant="red"
                    value={field.value || []}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    inputProps={{
                      placeholder: field.value?.length
                        ? undefined
                        : t`Ange ord du vill exkludera från resultaten`,
                      id,
                      "aria-invalid": fieldState.invalid,
                      autoComplete: "off",
                    }}
                  />

                  <Controller
                    name={`queries.${queryIndex}.not_near_distance`}
                    control={form.control}
                    render={({
                      field: distanceField,
                      fieldState: distanceFieldState,
                    }) => {
                      const distanceId = `form-qb-queries-${queryIndex}-not-near-distance`;
                      return (
                        <Popover>
                          <InputGroupAddon align="inline-end">
                            <PopoverTrigger
                              render={
                                <InputGroupButton
                                  aria-invalid={distanceFieldState.invalid}
                                  aria-label={t`Inställningar`}
                                  title={t`Inställningar`}
                                  size="icon-xs"
                                />
                              }
                            >
                              <Settings2Icon />
                            </PopoverTrigger>
                          </InputGroupAddon>
                          <PopoverContent className="w-96">
                            <Field data-invalid={distanceFieldState.invalid}>
                              <FieldLabel htmlFor={distanceId}>
                                <Trans>Avstånd</Trans>
                              </FieldLabel>

                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                  <Trans>Högst</Trans>
                                </span>
                                <Select
                                  value={distanceField.value?.toString() || ""}
                                  open={isNotNearDistanceSelectOpen}
                                  onOpenChange={setIsNotNearDistanceSelectOpen}
                                  onValueChange={(value) => {
                                    if (!value) {
                                      return;
                                    }

                                    distanceField.onChange(parseInt(value, 10));
                                  }}
                                >
                                  <SelectTrigger
                                    id={distanceId}
                                    aria-invalid={distanceFieldState.invalid}
                                  >
                                    <SelectValue
                                      placeholder={t`Välj avstånd`}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DISTANCE_VALUES.map((value) => (
                                      <SelectItem
                                        key={value}
                                        value={value.toString()}
                                      >
                                        {value}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <span className="text-sm text-muted-foreground truncate">
                                  {plural(distanceField.value || 0, {
                                    one: "ord bort från sökorden",
                                    other: "ord bort från sökorden",
                                  })}
                                </span>
                              </div>

                              {distanceFieldState.invalid && (
                                <FieldError
                                  errors={[distanceFieldState.error]}
                                />
                              )}
                            </Field>
                          </PopoverContent>
                        </Popover>
                      );
                    }}
                  />
                </InputGroup>

                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Controller
          name={`queries.${queryIndex}.sources`}
          control={form.control}
          render={({ field, fieldState }) => {
            const id = `form-qb-queries-${queryIndex}-sources`;

            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Medietyper</Trans>
                </FieldLabel>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    id={id}
                    render={
                      <Button
                        variant="outline"
                        className="text-left justify-between font-normal"
                        aria-invalid={fieldState.invalid}
                      />
                    }
                  >
                    {field.value?.length === sources.length ? (
                      <Trans>Alla valda</Trans>
                    ) : (
                      <span className="truncate">
                        {field.value
                          ?.map((source) =>
                            i18n._(SOURCE_CODE_TO_LABEL[source])
                          )
                          .join(", ")}
                      </span>
                    )}

                    <ChevronDownIcon className="text-muted-foreground opacity-50" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-(--anchor-width)">
                    {field.value?.length ? (
                      <DropdownMenuItem
                        id={`form-qb-queries-${queryIndex}-sources-uncheck-all`}
                        closeOnClick={false}
                        onClick={() => {
                          field.onChange([]);
                        }}
                      >
                        <Trans>Avmarkera alla</Trans>
                      </DropdownMenuItem>
                    ) : null}

                    {field.value?.length !== sources.length ? (
                      <DropdownMenuItem
                        id={`form-qb-queries-${queryIndex}-sources-check-all`}
                        closeOnClick={false}
                        onClick={() => {
                          field.onChange(sources);
                        }}
                      >
                        <Trans>Markera alla</Trans>
                      </DropdownMenuItem>
                    ) : null}

                    {socialSources.length ? (
                      <>
                        <DropdownMenuSeparator />

                        <DropdownMenuGroup>
                          <DropdownMenuLabel>
                            <Trans>Sociala medier</Trans>
                          </DropdownMenuLabel>

                          {socialSources.map((source) => {
                            const isChecked = field.value?.includes(source);

                            return (
                              <DropdownMenuCheckboxItem
                                id={`form-qb-queries-${queryIndex}-sources-${source}`}
                                key={source}
                                checked={isChecked}
                                onSelect={(e) => {
                                  e.preventDefault();
                                }}
                                onCheckedChange={(newIsChecked) => {
                                  let newSources: Source[] = field.value || [];

                                  if (newIsChecked) {
                                    newSources = [...newSources, source];
                                  } else {
                                    newSources = newSources.filter(
                                      (s) => s !== source
                                    );
                                  }

                                  field.onChange(newSources);
                                }}
                              >
                                {i18n._(SOURCE_CODE_TO_LABEL[source])}
                              </DropdownMenuCheckboxItem>
                            );
                          })}
                        </DropdownMenuGroup>
                      </>
                    ) : null}

                    {audioSources.length ? (
                      <>
                        <DropdownMenuSeparator />

                        <DropdownMenuGroup>
                          <DropdownMenuLabel>
                            <Trans>Ljud</Trans>
                          </DropdownMenuLabel>

                          {audioSources.map((source) => {
                            const isChecked = field.value?.includes(source);

                            return (
                              <DropdownMenuCheckboxItem
                                id={`form-qb-queries-${queryIndex}-sources-${source}`}
                                key={source}
                                checked={isChecked}
                                onSelect={(e) => {
                                  e.preventDefault();
                                }}
                                onCheckedChange={(newIsChecked) => {
                                  let newSources: Source[] = field.value || [];

                                  if (newIsChecked) {
                                    newSources = [...newSources, source];
                                  } else {
                                    newSources = newSources.filter(
                                      (s) => s !== source
                                    );
                                  }

                                  field.onChange(newSources);
                                }}
                              >
                                {i18n._(SOURCE_CODE_TO_LABEL[source])}
                              </DropdownMenuCheckboxItem>
                            );
                          })}
                        </DropdownMenuGroup>
                      </>
                    ) : null}

                    {newsSources.length ? (
                      <>
                        <DropdownMenuSeparator />

                        <DropdownMenuGroup>
                          <DropdownMenuLabel>
                            <Trans>Nyheter</Trans>
                          </DropdownMenuLabel>

                          {newsSources.map((source) => {
                            const isChecked = field.value?.includes(source);

                            return (
                              <DropdownMenuCheckboxItem
                                id={`form-qb-queries-${queryIndex}-sources-${source}`}
                                key={source}
                                checked={isChecked}
                                onSelect={(e) => {
                                  e.preventDefault();
                                }}
                                onCheckedChange={(newIsChecked) => {
                                  let newSources: Source[] = field.value || [];

                                  if (newIsChecked) {
                                    newSources = [...newSources, source];
                                  } else {
                                    newSources = newSources.filter(
                                      (s) => s !== source
                                    );
                                  }

                                  field.onChange(newSources);
                                }}
                              >
                                {i18n._(SOURCE_CODE_TO_LABEL[source])}
                              </DropdownMenuCheckboxItem>
                            );
                          })}
                        </DropdownMenuGroup>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>

                <FieldDescription>
                  <Trans>
                    Resultat från Meta, LinkedIn, X och nyheter är begränsade i
                    förhandsvisning.{" "}
                    <button
                      type="button"
                      disabled={!intercom.showArticle}
                      onClick={() => {
                        intercom.showArticle?.(
                          INTERCOM_ARTICLES.PREVIEW_RESULTS
                        );
                      }}
                      className="link"
                    >
                      Mer information
                    </button>
                  </Trans>
                </FieldDescription>

                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Controller
          name={`queries.${queryIndex}.languages`}
          control={form.control}
          render={({ field, fieldState }) => {
            const id = `form-qb-queries-${queryIndex}-languages`;

            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Språk</Trans>
                </FieldLabel>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    id={id}
                    render={
                      <Button
                        variant="outline"
                        className="text-left justify-between font-normal"
                        aria-invalid={fieldState.invalid}
                      />
                    }
                  >
                    {field.value?.length === languages.length ? (
                      <Trans>Alla valda</Trans>
                    ) : (
                      <span className="truncate">
                        {field.value
                          ?.map((language) =>
                            i18n._(LANGUAGE_CODE_TO_LABEL[language])
                          )
                          .join(", ")}
                      </span>
                    )}

                    <ChevronDownIcon className="text-muted-foreground opacity-50" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-(--anchor-width)">
                    {field.value?.length ? (
                      <DropdownMenuItem
                        closeOnClick={false}
                        onClick={() => {
                          field.onChange([]);
                        }}
                      >
                        <Trans>Avmarkera alla</Trans>
                      </DropdownMenuItem>
                    ) : null}

                    {field.value?.length !== languages.length ? (
                      <DropdownMenuItem
                        closeOnClick={false}
                        onClick={() => {
                          field.onChange(languages);
                        }}
                      >
                        <Trans>Markera alla</Trans>
                      </DropdownMenuItem>
                    ) : null}

                    <DropdownMenuSeparator />

                    {languages.map((language) => {
                      const isChecked = field.value?.includes(language);

                      return (
                        <DropdownMenuCheckboxItem
                          key={language}
                          checked={isChecked}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                          onCheckedChange={(newIsChecked) => {
                            let newLanguages: Language[] = field.value || [];

                            if (newIsChecked) {
                              newLanguages = [...newLanguages, language];
                            } else {
                              newLanguages = newLanguages.filter(
                                (l) => l !== language
                              );
                            }

                            field.onChange(newLanguages);
                          }}
                        >
                          {i18n._(LANGUAGE_CODE_TO_LABEL[language])}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>

                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Collapsible
          open={isAdvancedOpen}
          className={cn(
            "col-span-2",

            // Error state.
            "has-[[data-slot][aria-invalid=true]]:ring-[3px] has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40"
          )}
          onOpenChange={toggledAdvanced}
        >
          <div className="flex items-center gap-2">
            <ButtonGroup>
              <CollapsibleTrigger
                id={`form-qb-queries-${queryIndex}-advanced`}
                render={<Button size="sm" variant="secondary" />}
              >
                {isAdvancedOpen ? (
                  <Trans>Göm avancerade inställningar</Trans>
                ) : (
                  <Trans>Visa avancerade inställningar</Trans>
                )}
                <ChevronRightIcon
                  className={cn(
                    "transition-all",
                    isAdvancedOpen && "rotate-90"
                  )}
                />
              </CollapsibleTrigger>

              <AccessControl level={ACCESS_LEVELS.STAFF}>
                {canAddQuery ? (
                  <Button
                    size="sm"
                    type="button"
                    variant="secondary"
                    data-testid="form-qb-duplicate-query-button"
                    onClick={duplicateQuery}
                  >
                    <Trans>Duplicera</Trans>
                    <CopyIcon />
                  </Button>
                ) : null}
              </AccessControl>

              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={addQuery}
              >
                <Trans>Lägg till fler söktermer</Trans>
                <PlusIcon />
              </Button>
            </ButtonGroup>

            {queries.length > 1 ? (
              <Button
                size="sm"
                type="button"
                variant="secondary"
                onClick={deleteQuery}
              >
                <span className="sr-only">
                  <Trans>Ta bort</Trans>
                </span>
                <Trash2Icon />
              </Button>
            ) : null}

            {isLast ? (
              <Button
                type="submit"
                className="ml-auto"
                name={PreviewFormSubmitButton.Preview}
                disabled={isLoading || !formState.isDirty}
              >
                <Trans>Förhandsgranska</Trans>
                <RefreshCcwIcon />
              </Button>
            ) : null}
          </div>

          <CollapsibleContent
            className={cn(
              "mt-2 p-4 bg-muted-secondary rounded-lg",
              isAdvancedOpen ? "visible" : "hidden"
            )}
          >
            <div className="grid grid-cols-2 gap-x-4 gap-y-5">
              <Controller
                name={`queries.${queryIndex}.include_channel_names`}
                control={form.control}
                render={({ field, fieldState }) => {
                  const id = `form-qb-queries-${queryIndex}-include-channel-names`;

                  return (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={id}>
                        <Trans>Endast i källor</Trans>
                      </FieldLabel>
                      <BadgeInput
                        variant="cyan"
                        withBackground
                        value={(field.value?.filter(Boolean) as string[]) || []}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        inputProps={{
                          "aria-invalid": fieldState.invalid,
                          placeholder: field.value?.filter(Boolean)?.length
                            ? undefined
                            : t`Lägg till källor att begränsa resultatet till`,
                          autoComplete: "off",
                          id,
                        }}
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />

              <Controller
                name={`queries.${queryIndex}.exclude_channel_names`}
                control={form.control}
                render={({ field, fieldState }) => {
                  const id = `form-qb-queries-${queryIndex}-exclude-channel-names`;

                  return (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={id}>
                        <Trans>Exkludera källor</Trans>
                      </FieldLabel>
                      <BadgeInput
                        variant="red"
                        withBackground
                        value={(field.value?.filter(Boolean) as string[]) || []}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        inputProps={{
                          "aria-invalid": fieldState.invalid,
                          placeholder: field.value?.filter(Boolean)?.length
                            ? undefined
                            : t`Lägg till källor att exkludera`,
                          autoComplete: "off",
                          id,
                        }}
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />

              <Controller
                name={`queries.${queryIndex}.exclude_topics`}
                control={form.control}
                render={({ field, fieldState }) => {
                  const id = `form-qb-queries-${queryIndex}-exclude-topics`;

                  return (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={id}>
                        <Trans>Exkludera ämnen</Trans>
                      </FieldLabel>
                      <BadgeInput
                        variant="red"
                        withBackground
                        data-testid={id}
                        value={(field.value?.filter(Boolean) as string[]) || []}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        inputProps={{
                          placeholder: field.value?.filter(Boolean)?.length
                            ? undefined
                            : t`Lägg till ämnen att exkludera`,
                          id,
                          "aria-invalid": fieldState.invalid,
                          autoComplete: "off",
                        }}
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />

              <Controller
                name={`queries.${queryIndex}.mention_types`}
                control={form.control}
                render={({ field, fieldState }) => {
                  const id = `form-qb-queries-${queryIndex}-mention-types`;

                  return (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={id}>
                        <Trans>Välj typ av omnämnanden</Trans>
                      </FieldLabel>
                      <Select
                        value={field.value ?? undefined}
                        onValueChange={(value) => {
                          field.onChange(value as MentionSourceType);
                        }}
                      >
                        <SelectTrigger
                          id={id}
                          aria-invalid={fieldState.invalid}
                        >
                          <SelectValue placeholder={i18n._(t`Välj`)}>
                            {field.value ? (
                              field.value === MENTION_SOURCE_TYPE.ALL ? (
                                <Trans>Ljud & Text (förvalt)</Trans>
                              ) : (
                                i18n._(
                                  MENTION_SOURCE_TYPE_TO_LABEL[field.value]
                                )
                              )
                            ) : null}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="w-(--anchor-width)">
                          {MENTION_SOURCE_TYPES.map((mentionSourceType) => (
                            <SelectItem
                              key={mentionSourceType}
                              value={mentionSourceType}
                            >
                              {mentionSourceType === MENTION_SOURCE_TYPE.ALL ? (
                                <Trans>Ljud & Text (förvalt)</Trans>
                              ) : (
                                i18n._(
                                  MENTION_SOURCE_TYPE_TO_LABEL[
                                    mentionSourceType
                                  ]
                                )
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />

              {sources.includes(SOURCE.NEWS) && (
                <Controller
                  name={`queries.${queryIndex}.include_paywalled`}
                  control={form.control}
                  render={({ field, fieldState }) => {
                    const id = `form-qb-queries-${queryIndex}-include-paywalled`;

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>
                            Inkludera ofullständiga betalväggsträffar
                          </Trans>
                        </FieldLabel>

                        <Switch
                          id={id}
                          checked={field.value ?? false}
                          onCheckedChange={(newValue) => {
                            field.onChange(newValue);
                          }}
                        />

                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />
              )}

              <Controller
                name={`queries.${queryIndex}.tags`}
                control={form.control}
                render={({ field, fieldState }) => {
                  const id = `form-qb-queries-${queryIndex}-tags`;

                  return (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={id}>
                        <Trans>Tagga omnämnanden automatiskt</Trans>
                      </FieldLabel>

                      <Popover>
                        <PopoverTrigger
                          render={
                            <Button
                              id={id}
                              variant="outline"
                              className="text-left justify-between font-normal"
                              aria-invalid={fieldState.invalid}
                            />
                          }
                        >
                          {field.value?.length ? (
                            field.value.join(", ")
                          ) : (
                            <Trans>Lägg till taggar</Trans>
                          )}

                          <ChevronDownIcon className="text-muted-foreground opacity-50" />
                        </PopoverTrigger>
                        <PopoverContent className="w-(--anchor-width) p-0">
                          <TagSelector
                            queryIndex={queryIndex}
                            value={field.value || []}
                            newTags={newTags}
                            tags={tags}
                            onChange={(newTags) => {
                              field.onChange(newTags);
                            }}
                            onAddNewTag={(newTag) => {
                              setNewTags((prevNewTags) => [
                                ...prevNewTags,
                                newTag,
                              ]);
                            }}
                          />
                        </PopoverContent>
                      </Popover>

                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />

              {minReachByChannelTypeSources.length ? (
                <Controller
                  name={`queries.${queryIndex}.min_reach_by_channel_type`}
                  control={form.control}
                  render={({ field, fieldState }) => {
                    const id = `form-qb-queries-${queryIndex}-min-reach-by-channel-type`;

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>Minsta räckvidd</Trans>
                        </FieldLabel>

                        <Popover>
                          <PopoverTrigger
                            render={
                              <Button
                                id={id}
                                variant="outline"
                                className="text-left justify-between font-normal"
                                aria-invalid={fieldState.invalid}
                              />
                            }
                          >
                            <Trans>Hantera minsta räckvidd</Trans>

                            <ChevronDownIcon className="text-muted-foreground opacity-50" />
                          </PopoverTrigger>
                          <PopoverContent className="w-(--anchor-width) max-h-96 overflow-y-auto overscroll-y-contain flex flex-col gap-4">
                            <div className="flex flex-col gap-1.5">
                              <h4 className="font-medium">
                                <Trans>Minsta räckvidd</Trans>
                              </h4>
                              <p className="text-sm text-muted-foreground text-balance">
                                <Trans>
                                  Lägsta räckvidd för träffar. Noll betyder att
                                  alla träffar visas, oavsett räckvidd.
                                </Trans>
                              </p>
                            </div>

                            <Field>
                              <FieldLabel
                                htmlFor={`form-qb-queries-${queryIndex}-min-reach-by-channel-type-use-standard-values`}
                              >
                                <Trans>Använd standardvärden</Trans>
                              </FieldLabel>
                              <Switch
                                id={`form-qb-queries-${queryIndex}-min-reach-by-channel-type-use-standard-values`}
                                checked={
                                  field.value &&
                                  "has_custom_values" in field.value
                                    ? !field.value.has_custom_values
                                    : false
                                }
                                onCheckedChange={(newValue) => {
                                  const currentValue =
                                    field.value as MinReachByChannelTypeValues;

                                  field.onChange(
                                    newValue
                                      ? getMinReachByChannelTypeDefaultValues(
                                          minReachByChannelTypeSources
                                        )
                                      : {
                                          ...currentValue,
                                          has_custom_values: true,
                                        }
                                  );
                                }}
                              />
                            </Field>

                            {field.value &&
                            "has_custom_values" in field.value ? (
                              <div className="flex flex-col gap-2">
                                {minReachByChannelTypeSources.map((source) => {
                                  const currentValue =
                                    field.value as MinReachByChannelTypeValues;

                                  const value = currentValue[source];

                                  const id = `form-qb-queries-${queryIndex}-min-reach-${source}`;

                                  return (
                                    <Field
                                      orientation="horizontal"
                                      key={source}
                                    >
                                      <FieldLabel
                                        className="min-w-20"
                                        htmlFor={id}
                                      >
                                        {i18n._(SOURCE_CODE_TO_LABEL[source])}
                                      </FieldLabel>
                                      <Input
                                        id={id}
                                        type="text"
                                        value={
                                          typeof value === "number" &&
                                          !Number.isNaN(value)
                                            ? value.toLocaleString()
                                            : ""
                                        }
                                        disabled={
                                          !currentValue.has_custom_values
                                        }
                                        onChange={(e) => {
                                          const unformattedValue =
                                            e.currentTarget.value.replaceAll(
                                              /\D/g,
                                              ""
                                            );

                                          field.onChange({
                                            ...currentValue,
                                            [source]:
                                              parseInt(unformattedValue, 10) ||
                                              0,
                                          });
                                        }}
                                      />
                                    </Field>
                                  );
                                })}
                              </div>
                            ) : null}

                            <PopoverClose
                              render={<Button variant="secondary" />}
                            >
                              <Trans>Klar</Trans>
                            </PopoverClose>
                          </PopoverContent>
                        </Popover>

                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />
              ) : null}

              {isFirst ? (
                <AccessControl level={ACCESS_LEVELS.ADMIN}>
                  <Controller
                    name="information_need"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = "information-need";

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel staff htmlFor={id}>
                            <Trans>Smart relevans</Trans>
                          </FieldLabel>
                          <Textarea
                            id={id}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(e.currentTarget.value || null)
                            }
                            placeholder={t`Handlar det här om bananbröd, som i det bakade brödet?`}
                            className="bg-background dark:bg-input/30 resize-none"
                            aria-invalid={fieldState.invalid}
                            rows={4}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />
                </AccessControl>
              ) : null}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </FieldGroup>
  );
}
